import { CanAccess } from '@modules/common/components/CanAccess';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { validateEmail } from '@modules/common/utils';
import { InviteUsersRoleSelect } from '@modules/users/components/InviteUsersModal/InviteUsersRoleSelect';
import { useInviteUsersByEmail } from '@modules/users/hooks';
import { useUserManagementStore } from '@modules/users/store/hooks';
import { UserRowRoleType } from '@modules/users/store/types';
import { InviteUserRoleType } from '@modules/users/types';
import { trackUserManagementEvents } from '@modules/workspaceSettings/utils';
import { ForwardToInboxRounded } from '@mui/icons-material';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { SyntheticEvent, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

interface InviteUsersByEmailProps {
  shouldInviteAdminsOnly?: boolean;
}

export const InviteUsersByEmail = ({
  shouldInviteAdminsOnly = false,
}: InviteUsersByEmailProps) => {
  const {
    tenant: { domains: allowedDomains = [] },
  } = useGetUser();

  const users = useUserManagementStore(({ users }) => users);

  const canAccessWorkspaceSettings = useIsAuthorized({
    resource: PermissionResource.WorkspaceSettings,
    operation: PermissionOperation.Write,
  });

  const defaultRole = shouldInviteAdminsOnly
    ? UserRowRoleType.Admin
    : UserRowRoleType.Member;

  const [inputValue, setInputValue] = useState('');

  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] =
    useState<InviteUserRoleType>(defaultRole);

  const { closeModal } = useMuiModalContext();

  const { inviteUsers, isLoading: isInviteRequestInProgress } =
    useInviteUsersByEmail({
      onCompleted: () => {
        setSelectedEmails([]);
        setSelectedRole(defaultRole);
        closeModal();
      },
    });

  const onEmailsChange = (_: SyntheticEvent | null, emails: string[]) => {
    if (inputValue !== '') {
      setInputValue('');
    }
    setSelectedEmails(emails);
  };

  const onInputChange = (_: SyntheticEvent | null, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const handleChangeRole = (role: InviteUserRoleType) => {
    setSelectedRole(role);
  };

  const sendInvite = () => {
    inviteUsers({
      emails: selectedEmails,
      role: selectedRole,
    });
    trackUserManagementEvents({
      eventName: 'User Management - Invite Users: Clicked On Send Email Button',
      invitedUserEmails: selectedEmails,
    });
  };

  const errorText = (() => {
    const hasInvalidEmails = !!selectedEmails.find(
      (email) => !validateEmail(email, allowedDomains).isValid,
    );

    if (hasInvalidEmails) {
      return (
        'One or more emails are invalid. Allowed domains are: ' +
        allowedDomains?.join(', ')
      );
    }

    const isUserAlreadyInWorkspace = users.some((user) =>
      selectedEmails.includes(user.email),
    );

    if (isUserAlreadyInWorkspace) {
      return 'One or more emails already exist in the workspace.';
    }
    return '';
  })();

  const options = (() => {
    if (inputValue === '') {
      return [];
    }

    const domains = Array.from(new Set(allowedDomains));

    const [inputEmailPrefix, inputEmailDomain] = inputValue.split('@');

    const isInputEmailDomainAllowed = domains.some((domain) =>
      inputEmailDomain ? domain.startsWith(`@${inputEmailDomain}`) : false,
    );

    if (!inputEmailDomain || isInputEmailDomainAllowed) {
      return domains.map((domain) => `${inputEmailPrefix}${domain}`);
    }

    return [inputValue];
  })();

  const isDisabled = !selectedEmails.length || !!errorText;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <MailOutlineRoundedIcon />
        <Typography variant="subtitle1">Invite by Email</Typography>
      </Stack>
      <Grid container alignItems="flex-start" columnSpacing={2}>
        <Grid item xs={canAccessWorkspaceSettings ? 7 : 9.5} ml={1.75}>
          <Autocomplete
            freeSolo
            multiple
            autoHighlight
            size="small"
            value={selectedEmails}
            isOptionEqualToValue={(option, value) => option === value}
            filterOptions={(options, { inputValue }) => {
              if (options.length <= 1) {
                return options;
              }
              return options.filter((option) => option.startsWith(inputValue));
            }}
            options={options}
            limitTags={10}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Enter email IDs"
                error={!!errorText}
                helperText={errorText}
              />
            )}
            onChange={onEmailsChange}
            inputValue={inputValue}
            onInputChange={onInputChange}
            onBlur={(e) => {
              if (!inputValue.trim()) {
                setInputValue('');
                return;
              }
              onEmailsChange(e, [...selectedEmails, inputValue]);
            }}
          />
        </Grid>
        {canAccessWorkspaceSettings && (
          <Grid item xs={2.5}>
            <InviteUsersRoleSelect
              selectedRole={selectedRole}
              onChange={handleChangeRole}
              isDisabled={isDisabled || shouldInviteAdminsOnly}
              selectedEmails={selectedEmails}
            />
          </Grid>
        )}
        <Grid item xs={2}>
          <CanAccess
            resource={PermissionResource.Invite}
            operation={PermissionOperation.Write}
          >
            <LoadingButton
              size="small"
              fullWidth
              variant="contained"
              startIcon={<ForwardToInboxRounded />}
              onClick={sendInvite}
              loading={isInviteRequestInProgress}
              disabled={isDisabled}
              sx={{
                py: 0.9,
              }}
            >
              Send Invite
            </LoadingButton>
          </CanAccess>
        </Grid>
      </Grid>
    </Stack>
  );
};
